import mixpanel from 'mixpanel-browser';
import { getPrefixSSOID, isEmptyObject, MIXPANEL_EVENT, MIXPANEL_PROPERTY_SOURCE, MIXPANEL_PROPERTY_TYPE, MIXPANEL_VARIABLES } from './mixpanelUtils';

const Mixpanel = (() => {
  let userSsoId = null;
  let mixpanelJson = null;
  const mixpanelDebugMode = process.env.ADYEN_MIXPANEL_DEBUG === 'true';

  const fetchMixpanelJSON = async () => {
    try {
      const response = await fetch(process.env.ADYEN_MIXPANEL_JSON);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const getEventsMap = async () => {
    const mixpanelMap = await fetchMixpanelJSON();
    if(!mixpanelMap) return;

    const { events, metadata, superProperties, eventProperties, userProperties } = mixpanelMap;
    const mappedEvents = {
      events,
      superProperties: { ...superProperties.all, ...superProperties.web },
      eventProperties: { ...eventProperties.all, ...eventProperties.web },
      userProperties: { ...userProperties.all, ...userProperties.web },
      metadata: { ...metadata.all, ...metadata.web },
    };
    mixpanelJson = mappedEvents;

    return mappedEvents;
  };

  const readyMixpanelTracking = async () => {
    const mixpanelEventsMap = await getEventsMap();

    if(mixpanelEventsMap) {
      trackMixpanelEvent(MIXPANEL_EVENT.SubscribePaymentPage);
    };
  };

  const trackMixpanelEvent = (event) => {
    enqueueEvent({ eventName: event });
  };

  const enqueueEvent = async (event) => {
    const eventMap = mixpanelJson.events;
    const eventInMap = eventMap && eventMap.hasOwnProperty(event.eventName);
    if (!eventInMap) return;
    await processQueue(event);
  };

  const processQueue = async (event) => {
    if (mixpanelDebugMode) {
      console.log(`%c[Mixpanel] Processing #${event.eventName}`, 'color: blue');
    }

    await track(event);
  };

  const track = async (event) => {
    const { events: eventMap } = mixpanelJson;
    const { eventName } = event;
    const { setSuperProps, setUserProps, setEventProps, unionUserProps } = eventMap[eventName];

    // Super props
    const superProps = getProperties(MIXPANEL_PROPERTY_TYPE.Super, setSuperProps, event);
    if (!isEmptyObject(superProps)) mixpanel.register(superProps);

    switch (eventName) {
      case MIXPANEL_EVENT.SubscribePaymentPage:
        handleAnonymousUser();
        break;
      default:
        break;
    }

    // User props
    const userPropsToSet = getProperties(MIXPANEL_PROPERTY_TYPE.User, setUserProps, event);
    const userPropsToUnion = getProperties(MIXPANEL_PROPERTY_TYPE.User, unionUserProps, event);
    if (!isEmptyObject(userPropsToSet)) mixpanel.people.set(userPropsToSet);
    if (!isEmptyObject(userPropsToUnion)) mixpanel.people.union(userPropsToUnion);

    // Event props and Tracking
    // Added additional eventName prop to getProperties for debug logs, will be removed later
    const eventProps = getProperties(MIXPANEL_PROPERTY_TYPE.Event, setEventProps, event);
    if (isEmptyObject(eventProps)) {
      mixpanel.track(eventName);
    } else {
      mixpanel.track(eventName, eventProps);
    }
  };

  const getProperties = (propertyType, propertyList, event) => {
    if (!propertyList || propertyList.length === 0) return {};

    const properties = {};
    for (let i = 0; i < propertyList.length; i++) {
      let propertyName = propertyList[i];
      if (propertyName in mixpanelJson[propertyType]) {
        let property = mixpanelJson[propertyType][propertyName];
        let propertyValue = getPropertyValue(property);
        const notEmptyArray = Array.isArray(propertyValue) && propertyValue.length > 0;
        const valueExistAndNotArray =
          !Array.isArray(propertyValue) && typeof propertyValue !== 'undefined' && propertyValue !== '';

        if (valueExistAndNotArray || notEmptyArray) properties[propertyName] = propertyValue;
        valueChecker(event.eventName, propertyName, propertyValue);
      }
    }
    return properties;
  };

  const getPropertyValue = (property) => {
		const { source, variable } = property;

		switch (source) {
			case MIXPANEL_PROPERTY_SOURCE.Axis:
				return getAxisValue(variable);

			case MIXPANEL_PROPERTY_SOURCE.Client:
				return getClientValue(variable);

			default:
				return;
		}
	};

	const getAxisValue = variable => {
		const [source] = variable.split('|');

		switch (source) {
			case MIXPANEL_VARIABLES.SsoID:
				return userSsoId;

			default:
				return;
		}
	};

	const getClientValue = (variable) => {

		switch (variable) {
			case MIXPANEL_VARIABLES.UniqueUserId:
				return `mewatch-${userSsoId}`;

        default:
				return;
		}
	};

  const valueChecker = (name, variable, value) => {
    if (mixpanelDebugMode && (value === undefined || value === null)) {
      const debugString = `${name} var: ${variable} has no value`;
      console.log(`%c[Mixpanel] ${debugString}`, 'color: red');
    }
    return value;
  };

  const handleAnonymousUser = () => {
    mixpanel.identify(getPrefixSSOID(userSsoId));
    mixpanel.alias(mixpanel.get_distinct_id(), userSsoId);
  };

  return {
    initMixpanel: (ssoId) => {
      mixpanel.init(process.env.ADYEN_MIXPANEL_TOKEN, {
        debug: mixpanelDebugMode,
      });
      userSsoId = ssoId;
      readyMixpanelTracking();
    },
    trackMixpanelEvent: (event) => {
      enqueueEvent({ eventName: event });
    },
  };
})();

module.exports = Mixpanel;
